import React, { FC, useEffect } from 'react';
import { RateType } from 'QuoteApp/services/endpoints/GetRate';
import styles from './styles.module.css';
import { ReportMessage, ReportMessagePropsType } from './ReportMessage';
import { ReportType } from '../types';
import { useAppContext } from 'QuoteApp/services/providers';
import {
  getSelectedPaymentPlan,
  isPayInFullPlan,
} from 'QuoteApp/utils/paymentPlan';
import { RateChangeModalData } from '../useReportRunner';

export type ReportChangeMessageType = {
  content: string;
  messageType: ReportMessagePropsType['messageType'];
  reportType: ReportMessageIncidentType;
  adverseAction: boolean;
  modalRateChange?: RateChangeModalData;
};

export type ReportMessageIncidentType =
  | ReportType
  | 'accident-challenging'
  | 'current-carrier-challenging'
  | 'drivers-license-challenging';

type ModalRateChangeData = {
  initialInstallmentAmount: string;
  newInstallmentAmount: string;
  paymentFrequencyLabel: string;
};

const createModalRateChangeData = (
  initialRate: RateType | null,
  newRate: RateType | null
): ModalRateChangeData | undefined => {
  if (!initialRate || !newRate) {
    return undefined;
  }

  const hasRateChange = initialRate?.totalPremium !== newRate?.totalPremium;
  if (!hasRateChange) {
    return undefined;
  }

  const initialPaymentPlan = getSelectedPaymentPlan(initialRate);
  const newPaymentPlan = getSelectedPaymentPlan(newRate);

  if (!initialPaymentPlan || !newPaymentPlan) {
    return undefined;
  }

  const getDisplayAmount = (plan: NonNullable<typeof initialPaymentPlan>) =>
    isPayInFullPlan(plan) ? plan.totalAmount : plan.installmentAmount;

  return {
    initialInstallmentAmount: getDisplayAmount(initialPaymentPlan),
    newInstallmentAmount: getDisplayAmount(newPaymentPlan),
    paymentFrequencyLabel: newPaymentPlan.paymentFrequencyLabel,
  };
};

const getRateChangeMessage = (
  initialRate: RateType | null,
  newRate: RateType | null,
  reason: string = 'report results',
  reportType: ReportMessageIncidentType,
  adverseActionDisclosureText?: string
): ReportChangeMessageType | undefined => {
  const modalRateChange = createModalRateChangeData(initialRate, newRate);

  if (adverseActionDisclosureText) {
    return {
      content: adverseActionDisclosureText,
      messageType: 'rate-change',
      reportType: reportType,
      adverseAction: true,
      modalRateChange,
    };
  }

  if (
    !initialRate ||
    !newRate ||
    initialRate.totalPremium === newRate.totalPremium
  ) {
    return undefined;
  }

  return {
    content: `Rate changed from ${initialRate?.totalPremium} to ${newRate?.totalPremium} due to ${reason}.`,
    messageType: 'rate-change',
    reportType: reportType,
    // The BE sends the rate change message in the adverseActionDisclosureText if the rate increases; this covers
    // the case where the rate has decreased without counting it as an adverse action for the FCRA banner on Purchase
    adverseAction: false,
    modalRateChange,
  };
};

const getDownpayChangeMessage = (
  initialRate: RateType | null,
  newRate: RateType | null,
  reportType: ReportMessageIncidentType
): ReportChangeMessageType | undefined => {
  const initialRateHadDownpay =
    initialRate?.paymentDetails.attributes.paymentPlans.some(
      (plan) => plan.additionalDown
    );
  const newRateWithDownPay =
    newRate?.paymentDetails.attributes.paymentPlans.find(
      (plan) => plan.additionalDown
    );

  return initialRate &&
    newRate &&
    !initialRateHadDownpay &&
    !!newRateWithDownPay
    ? {
        content: `An initial downpayment of ${newRateWithDownPay.downPaymentAmount} will be required at bind.`,
        messageType: 'downpay-change',
        reportType: reportType,
        adverseAction: true,
      }
    : undefined;
};

const getFullPayChangeMessage = (
  initialRate: RateType | null,
  newRate: RateType | null,
  reportType: ReportMessageIncidentType
): ReportChangeMessageType | undefined => {
  // initial rate had other payment plans other than in full
  const initialRateHadOtherPlans =
    initialRate?.paymentDetails.attributes.paymentPlans.some(
      (plan) => plan.paymentFrequencyIdentifier !== 'in_full'
    );

  // expects back a single payment plan of only in full
  const newRateOnlyHasFullPay =
    newRate?.paymentDetails.attributes.paymentPlans.every(
      (plan) => plan.paymentFrequencyIdentifier === 'in_full'
    );

  return initialRate &&
    newRate &&
    initialRateHadOtherPlans &&
    newRateOnlyHasFullPay
    ? {
        content: `Customer must pay full 6-month premium of ${newRate.paymentDetails.attributes.paymentPlans[0].totalPremiumAndTaxAmount} at bind.`,
        messageType: 'fullpay-change',
        reportType: reportType,
        adverseAction: true,
      }
    : undefined;
};

export const getReportMessages = (
  initialRate: RateType | null,
  newRate: RateType | null,
  reason: string = 'report results',
  reportType: ReportMessageIncidentType,
  adverseActionDisclosureText?: string
): Array<ReportChangeMessageType> => {
  const rateChangeMessage = getRateChangeMessage(
    initialRate,
    newRate,
    reason,
    reportType,
    adverseActionDisclosureText
  );

  const downPayMessage = getDownpayChangeMessage(
    initialRate,
    newRate,
    reportType
  );

  const fullPayMessage = getFullPayChangeMessage(
    initialRate,
    newRate,
    reportType
  );

  return [
    ...(rateChangeMessage ? [rateChangeMessage] : []),
    ...(downPayMessage ? [downPayMessage] : []),
    ...(fullPayMessage ? [fullPayMessage] : []),
  ];
};

type ReportMessagesProps = {
  messages: Array<ReportChangeMessageType>;
  className?: string;
};

export const ReportMessages: FC<ReportMessagesProps> = ({
  messages,
  className,
}) => {
  const { changeDisplayFCRANotice } = useAppContext();
  useEffect(() => {
    if (messages.some((m) => m.adverseAction)) {
      changeDisplayFCRANotice(true);
    }
  }, [changeDisplayFCRANotice, messages]);
  return messages.length ? (
    <div className={styles.container} data-testid="report-messages">
      {messages.map((message, i) => (
        <ReportMessage
          key={`report-message-${i}`}
          messageType={message.messageType}
          reportType={message.reportType}
          className={className}
        >
          {message.content}
        </ReportMessage>
      ))}
    </div>
  ) : null;
};
